import { Drawer, DrawerOverlay, DrawerContent, Box } from "@chakra-ui/react";
import { ReactNode, createContext, useContext } from "react";
import { customEvent, EventProps } from "../../utils/customEvent";
import {
  VStack,
  baseColors,
  baseSpacing,
  HStack,
  Button,
  ColorIcon,
} from "goi_common";

interface BottomSheetContextValue {
  onClose: () => void;
  onBack?: () => void;
  closeEvent?: EventProps;
  backEvent?: EventProps;
  mainColor: string;
}

const BottomSheetContext = createContext<BottomSheetContextValue | null>(null);

const useBottomSheetContext = () => {
  const context = useContext(BottomSheetContext);
  if (!context) {
    throw new Error("바텀시트 context가 존재하지 않습니다.");
  }
  return context;
};

interface GoiBaseBottomSheetButton {
  text: ReactNode;
  onClick: () => void;
  event?: EventProps;
}

/**
 * GoiBaseBottomSheet - 템플릿 바텀시트 컴포넌트
 * 레이아웃이 고정된 바텀시트 컴포넌트. header/footer를 props로 받아 일관된 구조를 강제함
 * 일반 바텀시트 UI는 [정형] 패턴을 사용하고, 그 외의 경우 [커스텀] 패턴을 사용 (노션 모달 / 바텀시트 가이드 참고)
 *
 * @example
 * // 정형 패턴 (헤더/푸터 포함)
 * <GoiBaseBottomSheet
 *   isOpen={true}
 *   onClose={handleClose}
 *   onBack={handleBack}
 *   mainColor={baseColors.primary_orange}
 *   showCloseButton={true}
 *   header={
 *     <GoiBaseBottomSheet.Header
 *       title="제목"
 *       description="설명"
 *       showBackButton
 *     />
 *   }
 *   footer={
 *     <GoiBaseBottomSheet.Footer
 *       primaryButton={{ text: "확인", onClick: handleConfirm }}
 *     />
 *   }
 * >
 *   <div>컨텐츠</div>
 * </GoiBaseBottomSheet>
 *
 * @example
 * // 커스텀 패턴 (헤더/푸터 없이)
 * <GoiBaseBottomSheet
 *   isOpen={true}
 *   onClose={handleClose}
 *   onBack={handleBack}
 *   mainColor={baseColors.primary_orange}
 *   showCloseButton={true}
 * >
 *   <div>컨텐츠</div>
 * </GoiBaseBottomSheet>
 */
interface GoiBaseBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  children?: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  showCloseButton?: boolean;
  closeEvent?: EventProps;
  backEvent?: EventProps;
  closeOnOverlayClick?: boolean;
  mainColor?: string;
}

export const GoiBaseBottomSheet = ({
  isOpen,
  onClose,
  onBack,
  children,
  header,
  footer,
  showCloseButton = true,
  closeEvent,
  backEvent,
  closeOnOverlayClick = true,
  mainColor = baseColors.primary_orange,
}: GoiBaseBottomSheetProps) => {
  const handleClose = () => {
    if (closeEvent) customEvent(closeEvent);
    onClose();
  };

  const contextValue: BottomSheetContextValue = {
    onClose,
    onBack,
    closeEvent,
    backEvent,
    mainColor,
  };

  return (
    <BottomSheetContext.Provider value={contextValue}>
      <Drawer
        placement="bottom"
        isOpen={isOpen}
        onClose={handleClose}
        closeOnOverlayClick={closeOnOverlayClick}
        autoFocus={false}
        trapFocus={false}
      >
        <DrawerOverlay
          css={{
            backgroundColor: "rgba(0, 0, 0, 0.30)",
          }}
        />
        <DrawerContent
          sx={{
            width: "100% !important",
            maxWidth: "480px !important",
            margin: "0 auto",
            borderRadius: "30px 30px 0px 0px",
            boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.25)",
            maxHeight: "calc(100vh - 16px)",
          }}
        >
          {showCloseButton && (
            <Box
              onClick={handleClose}
              cursor="pointer"
              css={{
                position: "absolute",
                top: "20px",
                right: "20px",
                zIndex: 10,
              }}
            >
              <ColorIcon
                name="icon-x-mono"
                color={baseColors.gray_900}
                size={24}
              />
            </Box>
          )}
          {header && (
            <Box width="100%" padding="20px">
              {header}
            </Box>
          )}
          <Box
            width="100%"
            css={{
              overflowY: "auto",
              flex: 1,
            }}
          >
            {children}
          </Box>
          {footer && (
            <Box width="100%" padding="20px">
              {footer}
            </Box>
          )}
        </DrawerContent>
      </Drawer>
    </BottomSheetContext.Provider>
  );
};

/**
 * GoiBaseBottomSheet.Header - 바텀시트 헤더 컴포넌트
 * 바텀시트 상단에 제목, 설명, 뒤로가기 버튼을 표시하는 컴포넌트.
 * Context에서 onClose, onBack, closeEvent, backEvent를 자동으로 가져옴
 *
 * @example
 * <GoiBaseBottomSheet.Header
 *   title="제목"
 *   description="설명"
 *   showBackButton={true}
 * />
 */
const BottomSheetHeader = ({
  title,
  description,
  showBackButton = false,
}: {
  title: string;
  description?: string;
  showBackButton?: boolean;
}) => {
  const { onClose, onBack, closeEvent, backEvent } = useBottomSheetContext();

  const handleClose = () => {
    if (closeEvent) customEvent(closeEvent);
    onClose();
  };

  const handleBack = () => {
    if (backEvent) customEvent(backEvent);
    if (onBack) {
      onBack();
    } else {
      handleClose();
    }
  };

  const hasTitleOrDescription = title || description;

  return (
    <Box position="relative" width="100%">
      {/* 뒤로가기 + 타이틀만 (설명 없음) → 한 줄 배치 */}
      {showBackButton && title && !description && (
        <HStack gap={baseSpacing.sp_12} alignItems="center">
          <Box onClick={handleBack} cursor="pointer">
            <ColorIcon
              name="icon-arrow-left"
              color={baseColors.gray_900}
              size={24}
            />
          </Box>
          <Box className="headline_1_sb">{title}</Box>
        </HStack>
      )}

      {/* 뒤로가기 + (타이틀 / 설명) → 뒤로가기 위, 타이틀/설명 아래 배치 */}
      {showBackButton && hasTitleOrDescription && !(title && !description) && (
        <VStack width="100%" gap={baseSpacing.sp_16}>
          <Box onClick={handleBack} cursor="pointer">
            <ColorIcon
              name="icon-arrow-left"
              color={baseColors.gray_900}
              size={24}
            />
          </Box>

          <VStack gap={baseSpacing.sp_8} width="100%">
            {title && <Box className="headline_1_sb">{title}</Box>}
            {description && (
              <Box
                color={baseColors.label_alternative_1}
                className="body_1_normal_r"
              >
                {description}
              </Box>
            )}
          </VStack>
        </VStack>
      )}

      {/* 뒤로가기만 있는 경우 (타이틀 / 설명 X) */}
      {showBackButton && !hasTitleOrDescription && (
        <Box onClick={handleBack} cursor="pointer">
          <ColorIcon
            name="icon-arrow-left"
            color={baseColors.gray_900}
            size={24}
          />
        </Box>
      )}

      {/* 타이틀 / 설명만 있는 경우 (뒤로가기 X) */}
      {!showBackButton && hasTitleOrDescription && (
        <VStack gap={baseSpacing.sp_8} width="100%">
          {title && <Box className="headline_1_sb">{title}</Box>}
          {description && (
            <Box
              color={baseColors.label_alternative_1}
              className="body_1_normal_r"
            >
              {description}
            </Box>
          )}
        </VStack>
      )}
    </Box>
  );
};

/**
 * GoiBaseBottomSheet.Footer - 바텀시트 푸터 컴포넌트
 * 바텀시트 하단에 버튼을 표시하는 컴포넌트. Context에서 mainColor를 자동으로 가져옴
 *
 * @example
 * <GoiBaseBottomSheet.Footer
 *   primaryButton={{ text: "확인", onClick: handleConfirm }}
 *   secondaryButton={{ text: "취소", onClick: handleCancel }}
 *   buttonLayout="horizontal"
 * />
 */
const BottomSheetFooter = ({
  primaryButton,
  secondaryButton,
  buttonLayout = "horizontal",
}: {
  primaryButton: GoiBaseBottomSheetButton;
  secondaryButton?: GoiBaseBottomSheetButton;
  buttonLayout?: "horizontal" | "vertical";
}) => {
  const { mainColor } = useBottomSheetContext();

  const handleButtonClick = (button: GoiBaseBottomSheetButton) => {
    if (button.event) customEvent(button.event);
    button.onClick();
  };

  // 단일 CTA 버튼
  if (!secondaryButton) {
    return (
      <Button
        onClick={() => handleButtonClick(primaryButton)}
        className="body_1_normal_m"
        css={{
          width: "100%",
          height: "48px",
          borderRadius: "6px",
          background: mainColor,
          color: "white",
          cursor: "pointer",
        }}
      >
        {primaryButton.text}
      </Button>
    );
  }

  // 2개 CTA 버튼 (horizontal / vertical)
  const Stack = buttonLayout === "horizontal" ? HStack : VStack;
  const isHorizontal = buttonLayout === "horizontal";

  const primaryButtonElement = (
    <Button
      onClick={() => handleButtonClick(primaryButton)}
      className="body_1_normal_m"
      css={{
        ...(isHorizontal && { flex: 1 }),
        height: "48px",
        borderRadius: "6px",
        background: mainColor,
        color: "white",
        cursor: "pointer",
      }}
    >
      {primaryButton.text}
    </Button>
  );

  const secondaryButtonElement = (
    <Button
      onClick={() => handleButtonClick(secondaryButton)}
      className="body_1_normal_m"
      css={{
        ...(isHorizontal && { flex: 1 }),
        height: "48px",
        borderRadius: "6px",
        border: `1px solid ${baseColors.line_accent}`,
        background: baseColors.white,
        color: baseColors.label_alternative_1,
        cursor: "pointer",
      }}
    >
      {secondaryButton.text}
    </Button>
  );

  return (
    <Stack width="100%" gap={baseSpacing.sp_8}>
      {isHorizontal ? (
        <>
          {secondaryButtonElement}
          {primaryButtonElement}
        </>
      ) : (
        <>
          {primaryButtonElement}
          {secondaryButtonElement}
        </>
      )}
    </Stack>
  );
};

GoiBaseBottomSheet.Header = BottomSheetHeader;
GoiBaseBottomSheet.Footer = BottomSheetFooter;
